// ── AccountStore — Zustand store for Kalshi account state (MT relay) ──
//
// In the MT world, all Kalshi operations go through the relay API.
// Keys are stored server-side. The browser never signs requests directly.
// This store preserves the same state shape so views don't change.

import { create } from 'zustand';
import { apiFetch } from './authStore';
import {
  canEditKalshiOrder,
  type KalshiPosition,
  type KalshiFill,
  type KalshiOrder,
  type OrderInput,
  type OscarSequenceInput,
  type OscarSequenceResult,
  type KeyHealth,
  type AccountStatus,
} from '../types/kalshi';
import { readSoundSettings } from '../lib/audio/soundSettings';
import { getChannelVolume } from '../lib/alerts/alertPreferences';
import type { AlertChannelId } from '../lib/alerts/registry';
import { playFillTone, playKaChingTone, playWhaleTone, playOrderRejectedTone, playOrderPlacedTone } from '../lib/audio/tradeAudio';
import { getAudioContext, setPendingSound } from '../lib/audio/audioContext';
import { LimitRejectionError, isLimitRejectionPayload } from '../lib/limits';
import { mirrorCents, validateReplacePriceCents } from '../lib/pricing/subCent';
import { residualOpenContracts, MAX_CLOSE_ATTEMPTS } from '../lib/trading/closeResidual';
import { OrderSubmissionUnknownError } from '../lib/trading/orderSubmissionOutcome';
import {
  KALSHI_CANCEL_SYNC_TIMEOUT_MS,
  beginKalshiCancel,
  clearKalshiCancel,
  isKalshiCancelPending,
  markKalshiCancelAwaitingRemoval,
  markKalshiCancelTimedOut,
  reconcileKalshiCancels,
  type KalshiCancelBatchResult,
  type KalshiCancelStates,
} from '../lib/trading/kalshiCancelLifecycle';
import { reconstructOrderTraderSide } from '../lib/kalshiApi/orderReadV2';
import { startVisibilityAwareInterval } from '../lib/visibilityThrottle';
import { onActiveChange, subscribePush, topicActive } from '../services/relayPush';
import { readKalshiReportedBalanceCents } from '../lib/kalshiBalance';
// One bound for the Recent Fills tape. The rail merges this Kalshi slice with
// the Poly relay read and caps the union at the same number, so the slice size
// here and the Poly request limit must track one constant (#457).
import { RECENT_FILL_LIMIT } from '../lib/fills/recentFillTape';

const POLL_INTERVAL_MS = 5000;
export const KALSHI_PLACEMENT_CONFIRMATION_TIMEOUT_MS = 10_000;
// The relay's reconciliation backstop discovers fills every 10s when its
// Kalshi fills WebSocket is unavailable. A 10s client cutoff raced that same
// cadence: request, ingest, and delivery overhead could make a legitimate
// newly observed fill 10.001s old and silently suppress its sound (#528).
// Three backstop windows keep that boundary audible while remaining far too
// short to turn historical ledger hydration into an alert burst.
const FILL_SOUND_RECENCY_MS = 30_000;
// A fresh page/session immediately hydrates recent fills through REST. Those
// rows predate the session and must stay silent even when they fall inside the
// reconciliation window above. Automatic reconnects intentionally preserve
// this baseline so fills created during a transient gap can still alert once
// discovered; an explicit disconnect followed by connect starts a new session.
let _fillSoundSessionStartedAtMs: number | null = null;
const MAX_SEEN_FILLS = 1000;
const MAX_RECENT_FILLS = 100;

let _stopPollTimer: (() => void) | null = null;
let _connectRetryTimer: ReturnType<typeof setTimeout> | null = null;
let _connectRetryAttempts = 0;
let _connectionGeneration = 0;
const MAX_CONNECT_RETRIES = 5;
const BASE_CONNECT_RETRY_MS = 3_000;
// After fast retries exhaust, keep trying at a gentle cadence instead of
// dying permanently (status 'error' previously required a manual reload).
const SLOW_CONNECT_RETRY_MS = 60_000;
let _connectInFlight = false;

// Self-heal watchdog. connect()'s generation guards early-return on a stale
// generation (a disconnect()/remount that straddled an in-flight connect). That
// bare `return` skips BOTH the success path (which starts polling) AND the catch
// block (which schedules the retry ladder) — so a straddled connect leaves the
// store stranded at a non-'connected' status with NO retry timer and nothing to
// re-trigger it. That is the "API key: Connecting…" hang that only clears on a
// manual reload or by refreshing another window (the multi-window reconnect
// dance, Jun 27 diagnosis). The watchdog re-fires connect() ONLY when a
// connection is provably wanted and nothing is working toward it, so it can
// never compete with an active connect/retry or churn a healthy session.
let _desiredConnected = false;
let _healWatchdog: ReturnType<typeof setInterval> | null = null;
const HEAL_WATCHDOG_INTERVAL_MS = 10_000;

const _seenFillIds = new Set<string>();
const _seenFillOrder: string[] = [];
const _seenOrderIds = new Set<string>();

// ── #241 cancel fence bookkeeping ───────────────────────────────────────────
// Timers and attempt tokens live outside the store: they're machinery, not
// rendered state. Every cancel attempt gets a monotonic token, and the timer
// entry carries the token that armed it — so a DELETE that resolves late, or a
// timeout that fires after the row was reconciled away / retried / disconnected,
// can prove it is stale and do nothing instead of mutating a newer attempt.
let _cancelAttemptSeq = 0;
const _cancelAttempts = new Map<string, number>();
const _cancelTimers = new Map<string, { token: number; handle: ReturnType<typeof setTimeout> }>();

// A successful placement response is the first authoritative evidence that a
// resting order exists. Kalshi's immediately-following open-orders read can lag
// that write briefly, so keep the accepted order projected until one later
// authoritative snapshot confirms its id or this short fence expires. This is
// display reconciliation only: it never retries or changes an order payload.
interface PlacementConfirmation {
  order: KalshiOrder;
  expiresAtMs: number;
}
const _placementConfirmations = new Map<string, PlacementConfirmation>();

function forgetPlacementConfirmation(orderId: string): void {
  _placementConfirmations.delete(orderId);
}

function forgetAllPlacementConfirmations(): void {
  _placementConfirmations.clear();
}

/** Whether an order shown in `openOrders` is still only the short-lived local
 * placement projection and has not yet appeared in an authoritative venue
 * snapshot. LT2's reconciliation timer uses this distinction so the projection
 * cannot accidentally count as proof of its own order. */
export function hasPendingKalshiPlacementConfirmation(
  orderId: string,
  nowMs = Date.now(),
): boolean {
  const confirmation = _placementConfirmations.get(orderId);
  if (!confirmation) return false;
  if (confirmation.expiresAtMs <= nowMs) {
    _placementConfirmations.delete(orderId);
    return false;
  }
  return true;
}

function reconcilePlacementConfirmations(
  authoritative: KalshiOrder[],
  nowMs = Date.now(),
): KalshiOrder[] {
  const ids = new Set(authoritative.map((order) => order.orderId));
  let next: KalshiOrder[] | null = null;
  for (const [orderId, confirmation] of _placementConfirmations) {
    if (ids.has(orderId)) {
      _placementConfirmations.delete(orderId);
      continue;
    }
    if (confirmation.expiresAtMs <= nowMs) {
      _placementConfirmations.delete(orderId);
      continue;
    }
    if (!next) next = authoritative.slice();
    next.push(confirmation.order);
  }
  return next ?? authoritative;
}

function clearCancelTimer(orderId: string): void {
  const timer = _cancelTimers.get(orderId);
  if (timer) {
    clearTimeout(timer.handle);
    _cancelTimers.delete(orderId);
  }
}

function forgetCancelAttempt(orderId: string): void {
  _cancelAttempts.delete(orderId);
  clearCancelTimer(orderId);
}

/** Arm the bounded escape from a stalled Open Orders synchronization for one
 *  claimed attempt. Shared by the single and batch paths so the two can never
 *  drift on which attempt a late timer is allowed to promote. */
function armCancelSyncTimer(orderId: string, token: number): void {
  const handle = setTimeout(() => {
    const timer = _cancelTimers.get(orderId);
    if (!timer || timer.token !== token) return;
    _cancelTimers.delete(orderId);
    if (_cancelAttempts.get(orderId) !== token) return;
    // Hands the control back with truthful copy. Never removes the row —
    // we don't know that the order is gone, only that we can't confirm it.
    useAccountStore.setState({
      kalshiCancelStates: markKalshiCancelTimedOut(
        useAccountStore.getState().kalshiCancelStates,
        orderId,
      ),
    });
  }, KALSHI_CANCEL_SYNC_TIMEOUT_MS);
  _cancelTimers.set(orderId, { token, handle });
}

function forgetAllCancelAttempts(): void {
  for (const timer of _cancelTimers.values()) clearTimeout(timer.handle);
  _cancelTimers.clear();
  _cancelAttempts.clear();
}

/** Reconcile the fence against a snapshot that has ALREADY been validated by
 *  parseOrdersSnapshot, and drop the machinery for every ID it retires. Callers
 *  fold the result into the same `set` that writes openOrders, so the list and
 *  the fence can never disagree for a frame. */
function reconcileCancelFence(
  states: KalshiCancelStates,
  orders: KalshiOrder[],
): KalshiCancelStates {
  const next = reconcileKalshiCancels(states, orders.map((order) => order.orderId));
  if (next === states) return states;
  for (const orderId of Object.keys(states)) {
    if (next[orderId] === undefined) forgetCancelAttempt(orderId);
  }
  return next;
}

/** The bare DELETE — no fence, no refresh. Split out for #272: a batch must be
 *  able to send N deletes and then refresh ONCE, and the per-ID refresh below
 *  would otherwise fire N times against the same org snapshot. */
async function deleteKalshiOrder(orderId: string): Promise<void> {
  const res = await apiFetch(`/api/kalshi/orders/${orderId}`, { method: 'DELETE' });
  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error((errData as Record<string, string>).error ?? `Cancel failed (${res.status})`);
  }
}

/** The raw cancel leg — DELETE, surface the venue's real error, kick the
 *  fire-and-forget refresh. NO fence: `replaceOrder`'s cancel-first edit path
 *  calls this directly so a price edit can't disable the row it is editing or
 *  raise the cancel-timeout notice. Only the public `cancelOrder`/`cancelOrders`
 *  fence. */
async function requestKalshiCancel(orderId: string): Promise<void> {
  await deleteKalshiOrder(orderId);
  forgetPlacementConfirmation(orderId);
  useAccountStore.getState().refreshOrders().catch(() => {});
}

const CANCEL_ALREADY_PENDING_MESSAGE =
  'Cancel already pending; waiting for the authoritative Open Orders list.';

/** Health of the open-orders refresh loop. Surfaced on the positions page
 *  so a silently failing/stale order list is VISIBLE — the Jun 11 incident:
 *  a trader's orders were resting at Kalshi (intent tracker proved it) while
 *  the list sat empty with zero feedback, so he re-placed the same order 4x
 *  and stacked real exposure. Never fail this loop silently again. */
export interface OrdersSyncState {
  lastSuccessMs: number | null;
  lastErrorMs: number | null;
  lastError: string | null;
  consecutiveFailures: number;
}

interface AccountState {
  status: AccountStatus;
  error: string | null;
  ordersSync: OrdersSyncState;
  keyHealth: KeyHealth[];
  credentialCount: number;
  keyMode: 'org' | 'user' | null;

  balance: number | null;
  positions: KalshiPosition[];
  openOrders: KalshiOrder[];
  /** #241 — per-order cancel fence, keyed by order ID. A row is disabled while
   *  its entry is pending; the entry only clears when a successfully parsed
   *  authoritative snapshot stops listing the ID (or the 5,000 ms sync timeout
   *  hands the control back). Kalshi-only; Polymarket cancels are hook-local. */
  kalshiCancelStates: KalshiCancelStates;
  recentFills: KalshiFill[];
  activityTicker: KalshiFill[];
  /** #499 — true once a VALIDATED fills snapshot succeeded this session. The
   *  connection status cannot carry this: `status === 'connected'` holds even
   *  while every fills read fails or arrives malformed, so it must never back
   *  a "No recent fills" claim. Latches until disconnect, matching the
   *  retained-rows philosophy of the combined tape. */
  fillsSynced: boolean;
  realizedPnlCents: number;
  realizedFeesCents: number | null;
  realizedPnlNetCents: number | null;
  openNotionalCents: number;

  connect: () => Promise<void>;
  disconnect: () => void;
  /** Resolves true only when it FRESHLY set positions this call (connected,
   *  same connection generation, HTTP 200) — false on any no-op/stale path so a
   *  caller can tell "confirmed flat" from "couldn't read". */
  refreshPositions: () => Promise<boolean>;
  refreshOrders: () => Promise<void>;
  refreshPnl: () => Promise<void>;
  refreshFills: () => Promise<void>;
  processFill: (fill: KalshiFill) => void;
  placeOrder: (input: OrderInput) => Promise<KalshiOrder>;
  /** Oscar overtake/blocker grouped sequence. Resolves with per-leg statuses
   *  (HTTP 200 once execution begins — check result.ok/legs); throws on
   *  pre-flight rejections (validation, permission, limit envelope). */
  placeOrderSequence: (input: OscarSequenceInput) => Promise<OscarSequenceResult>;
  /** Market-close a single (ticker, side) leg with verify-and-retry. Each
   *  attempt fires a reduce_only marketable-IOC sell (type:'market' → IOC at
   *  the 1¢ slippage floor) and reads the residual off the order response. The
   *  IOC cancels its unfilled remainder, so `requested − fill_count` is the
   *  authoritative residual; we re-sweep ONLY what's left, up to
   *  MAX_CLOSE_ATTEMPTS, then surface it. Resolves to 0 (confirmed flat), >0
   *  (exposure still open after exhausting attempts — caller must warn loudly),
   *  or null (the response carried no fill counts, opaque — do NOT blind-retry).
   *  Rejects if a POST itself fails (network / limit / API error). */
  sweepCloseLeg: (ticker: string, side: 'yes' | 'no', quantity: number) => Promise<number | null>;
  cancelOrder: (orderId: string) => Promise<void>;
  /** #272 — batch cancel riding the SAME #241 fence. Claims every ID in one
   *  synchronous write before the first DELETE, dedupes, runs the venue's
   *  requests sequentially, and refreshes ONCE (pass `refreshAfter: false` when
   *  a mixed-venue caller owns the per-venue refresh). Never throws for an
   *  individual order — the per-ID result carries `submitted` / `failed` /
   *  `already-pending` plus the venue's raw error, so a partial failure stays
   *  visible and retryable instead of collapsing into one message. */
  cancelOrders: (
    orderIds: readonly string[],
    options?: { refreshAfter?: boolean },
  ) => Promise<readonly KalshiCancelBatchResult[]>;
  /** Game-wide PULL fast path: ONE relay round trip
   *  (`POST /api/kalshi/orders/cancel-sequence`) cancels every resting order
   *  the caller has on the given tickers — the relay derives the target set
   *  itself and fans out the venue DELETEs with no client pacing. Rides the
   *  same #241 fence as `cancelOrders` (locally-known ids claimed in one
   *  synchronous write before the POST). A 404 from an older relay releases
   *  the claims and falls back to the sequential `cancelOrders` path —
   *  byte-identical old behavior. */
  cancelOrdersByTickers: (
    tickers: readonly string[],
  ) => Promise<readonly KalshiCancelBatchResult[]>;
  /** Queue-preserving partial cancel via the relay's /orders/:id/decrease
   *  proxy. Keeps the order's orderId + FIFO position; only the remaining
   *  count shrinks. `nextQuantity` must be an integer in [1, currentRemaining).
   *  Throws on validation or relay errors. Used by `replaceOrder`'s smart
   *  route when only qty shrinks at the same price. */
  decreaseOrder: (orderId: string, nextQuantity: number) => Promise<void>;
  /** Cancel + re-place an open Kalshi order at a new price and/or quantity.
   *  Smart-routes by default: when only the quantity shrinks (same price,
   *  smaller remaining), calls `decreaseOrder` instead — preserves queue
   *  position. Otherwise (price moved, qty increased, both changed) cancel
   *  + place, preserving ticker/side/action/expirationTs. Pass
   *  `forceCancelReplace: true` to bypass the smart route entirely —
   *  always cancel + place, even on a pure qty-shrink. Used when the
   *  trader explicitly wants to send the order to the back of the queue
   *  (the "Cycle" semantic). Throws with `'Could not cancel original
   *  order: …'` if the cancel leg fails, or `'Original order was cancelled,
   *  but the replacement failed to place: …'` if the place leg fails
   *  after the cancel. Resolves with the id of the order left resting:
   *  the original id on the no-op and /decrease routes, the freshly
   *  placed id on cancel+place — callers keying UI state off the order
   *  id (pinned cards) transfer on that. */
  replaceOrder: (
    orderId: string,
    newPriceCents: number,
    newQuantity?: number,
    forceCancelReplace?: boolean,
  ) => Promise<{ orderId: string }>;
  checkCredentials: () => Promise<void>;
}

function stopPolling() {
  if (_stopPollTimer) { _stopPollTimer(); _stopPollTimer = null; }
}

/** The store's core data topics — when ALL of them stream from the relay
 *  push channel, the 5-request poll tick stands down entirely. Any gap
 *  (stream down, or a topic the server can't serve for this scope) re-arms
 *  polling via the onActiveChange watcher below. */
function corePushActive(): boolean {
  return (
    topicActive('positions') &&
    topicActive('orders') &&
    topicActive('fills') &&
    topicActive('pnl')
  );
}

let _lastPollFn: (() => void) | null = null;

function startPolling(poll: () => void) {
  stopPolling();
  _lastPollFn = poll;
  poll(); // initial snapshot always via REST — the reconnect-gap filler
  if (corePushActive()) return; // stream-fed; watcher re-arms on push loss
  // Hidden tabs (and popped-out FillsPopup contexts) run this
  // 5-request tick at 1/6 rate; one immediate tick fires on refocus.
  _stopPollTimer = startVisibilityAwareInterval(poll, POLL_INTERVAL_MS);
}

onActiveChange(() => {
  if (!_lastPollFn) return;
  if (useAccountStore.getState().status !== 'connected') return;
  if (corePushActive()) {
    stopPolling();
  } else if (!_stopPollTimer) {
    // Push dropped mid-session: one immediate poll heals the gap, then the
    // normal cadence resumes until the stream returns.
    _lastPollFn();
    _stopPollTimer = startVisibilityAwareInterval(_lastPollFn, POLL_INTERVAL_MS);
  }
});

function nextConnectionGeneration() {
  _connectionGeneration += 1;
  return _connectionGeneration;
}

function isCurrentConnectionGeneration(generation: number) {
  return _connectionGeneration === generation;
}

// Starts the self-heal watchdog once (idempotent). Gated to do no harm — it
// only nudges connect() back to life on a provably-stranded session:
//   • _desiredConnected — a connect() was requested and not since disconnected,
//     so it stays silent after logout/disconnect (which set it false).
//   • !_connectInFlight && !_connectRetryTimer — nothing is already trying. The
//     retry ladder owns the 429-backoff path and keeps a timer alive even in
//     'error'; we never compete with it. Among credentialed sessions, the only
//     state with no in-flight + no timer + not connected is the stale-generation
//     strand (the keyless canTrade:false rest state also matches the timer
//     conditions but is excluded by the credentialCount guard below).
//   • credentialCount > 0 — the trader has keys (no creds → no reconnect churn).
//   • status !== 'connected' — only act on a stranded session, never churn a
//     healthy one.
function ensureHealWatchdog(getState: () => AccountState) {
  if (_healWatchdog) return;
  _healWatchdog = setInterval(() => {
    if (!_desiredConnected) return;
    if (_connectInFlight || _connectRetryTimer) return;
    const st = getState();
    if (st.credentialCount <= 0) return;
    if (st.status === 'connected') return;
    void st.connect();
  }, HEAL_WATCHDOG_INTERVAL_MS);
}

function pickStr(...c: unknown[]) { for (const v of c) if (typeof v === 'string' && v !== '') return v; return undefined; }
function pickNum(...c: unknown[]) { for (const v of c) { if (typeof v === 'number' && !isNaN(v)) return v; if (typeof v === 'string' && v !== '') { const n = parseFloat(v); if (!isNaN(n)) return n; } } return undefined; }
function pickWholeContracts(...c: unknown[]) {
  const n = pickNum(...c);
  return n == null ? undefined : Math.max(0, Math.round(n));
}

/**
 * POST an order to the relay and return the RAW Kalshi order object
 * (`data.order ?? data`) — the unmapped response, so callers can read fill /
 * remaining counts off it. Throws a typed `LimitRejectionError` on the
 * structured limit-rejection envelope, else a generic Error. No sounds, no
 * refresh — the caller owns those side effects.
 */
async function postOrderRaw(input: OrderInput): Promise<Record<string, unknown>> {
  // `input.exchangeIndex` is observation context, not write authority. #575
  // owns the eventual exchange_index=-1 venue contract; this explicit builder
  // intentionally leaves the observed value off today's relay wire.
  const body: Record<string, unknown> = {
    ticker: input.ticker,
    side: input.side,
    action: input.action,
    type: input.type,
    count: input.count,
    count_fp: String(input.count),
  };
  if (input.price !== undefined) {
    // 4 dp carries the 0.01¢ grain exactly (sub-cent deci_cent markets);
    // toFixed(2) silently quantized to whole cents.
    body[`${input.side}_price_dollars`] = (input.price / 100).toFixed(4);
  }
  if (input.postOnly) body.post_only = true;
  if (input.timeInForce) body.time_in_force = input.timeInForce;
  if (input.expirationTime) body.expiration_ts = Math.floor(new Date(input.expirationTime).getTime() / 1000);
  // reduce_only guards a close from over-filling past flat into an opposite
  // position (the relay translator forwards body.reduce_only into the V2 body).
  if (input.reduceOnly) body.reduce_only = true;
  if (input.clientOrderId) body.client_order_id = input.clientOrderId;
  // Audit-only stamp: stored verbatim in trades.request_payload; the relay's
  // legacyToV2Order allowlist keeps it off the venue wire.
  if (input.auditMeta) body.__lt2 = input.auditMeta;
  // Attack gate marker (V3): the relay checks grant + ceiling + depth on the
  // key's presence; same venue-allowlist strip as __lt2.
  if (input.attackMeta) body.__attack = input.attackMeta;

  let res: Response;
  try {
    res = await apiFetch('/api/kalshi/orders', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    // The browser lost the relay response after submission began. The same
    // client_order_id may already be live; never present this as a rejection.
    throw new OrderSubmissionUnknownError();
  }
  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    // Structured limit-rejection envelope from relay/checkOrderLimit. Throw a
    // typed error so order forms can render richer UI than the generic toast
    // (breach details: rule, cap, current, requested).
    if (isLimitRejectionPayload(errData)) {
      throw new LimitRejectionError(errData.error, errData.code, errData.breach);
    }
    if ((errData as Record<string, unknown>).code === 'KALSHI_ORDER_STATE_UNKNOWN') {
      throw new OrderSubmissionUnknownError(
        typeof (errData as Record<string, unknown>).error === 'string'
          ? String((errData as Record<string, unknown>).error)
          : undefined,
      );
    }
    // A front proxy can lose the relay's structured envelope during the same
    // ambiguous post-submit failure (for example, a restart returning an HTML
    // 502/504). The venue may still hold the client_order_id.
    if (res.status === 408 || res.status >= 500) {
      throw new OrderSubmissionUnknownError();
    }
    throw new Error((errData as Record<string, string>).error ?? `Order failed (${res.status})`);
  }
  let data: Record<string, unknown>;
  try {
    data = (await res.json()) as Record<string, unknown>;
  } catch {
    // The relay reported success but the response was truncated before the
    // order identity arrived. Acceptance is possible; never invite a retry.
    throw new OrderSubmissionUnknownError();
  }
  return (data.order ?? data) as Record<string, unknown>;
}

function dollarsToCents(dollars: unknown, fallbackCents?: unknown): number {
  // 0.01¢ grain preserved — whole-cent rounding here fed rounded prices into
  // order display and PnL reads (sub-cent contract, 2026-07-17).
  if (typeof dollars === 'string' && dollars !== '') { const n = parseFloat(dollars); if (!isNaN(n)) return Math.round(n * 10000) / 100; }
  if (typeof dollars === 'number') return dollars <= 1.01 ? Math.round(dollars * 10000) / 100 : Math.round(dollars * 100) / 100;
  if (typeof fallbackCents === 'number') return Math.round(fallbackCents * 100) / 100;
  return 0;
}

// Like dollarsToCents but preserves centicent precision — Kalshi reports
// fees_paid_dollars as a 4-decimal string (e.g. "0.0734") and rounding to
// whole cents would silently drop sub-cent fee accruals.
function parseFeesPaidCents(dollars: unknown, fallbackCents?: unknown): number {
  if (typeof dollars === 'string' && dollars !== '') {
    const n = parseFloat(dollars);
    if (!isNaN(n)) return n * 100;
  }
  if (typeof dollars === 'number' && Number.isFinite(dollars)) return dollars * 100;
  if (typeof fallbackCents === 'number' && Number.isFinite(fallbackCents)) return fallbackCents;
  return 0;
}

// Exported so org-wide surfaces (e.g. the manager Org-Account view) parse raw
// Kalshi /portfolio/positions with the SAME field handling (position_fp,
// *_price_dollars, zero-qty filtering) instead of re-hand-rolling it.
export function parsePositions(data: Record<string, unknown>): KalshiPosition[] {
  const raw = (data.market_positions ?? data.positions ?? data.results ?? data.data ?? []) as Record<string, unknown>[];
  if (!Array.isArray(raw)) return [];
  return raw.map((p): KalshiPosition | null => {
    const ticker = pickStr(p.ticker, p.market_ticker);
    if (!ticker) return null;

    // V2 shape from /api/trader/positions: { ticker, side, netCount, avgEntryPriceCents, realizedPnlCents }
    if ('netCount' in p) {
      const position = pickNum(p.netCount) ?? 0;
      if (position <= 0) return null;
      return {
        ticker,
        side: (p.side as 'yes' | 'no') ?? 'yes',
        position,
        averagePrice: pickNum(p.avgEntryPriceCents) ?? 0,
        lastFillAt: pickStr(p.lastFillAt, p.last_fill_at) || null,
        costBasisCents: pickNum(p.costBasisCents),
        realizedPnl: pickNum(p.realizedPnlCents) ?? 0,
        // Per-user fee sums from the relay's fills ledger; undefined when no
        // contributing fill carried a fee field, so the UI distinguishes
        // "unknown" from "actually $0.00".
        feesPaidCents: pickNum(p.feesPaidCents),
        // Open-share entry fees — the unrealized-recipe fee input. The bucket
        // total above double-counts the sold share against realized-net on
        // partially-closed positions.
        openFeesCents: pickNum(p.openFeesCents),
        // Full-position anchor (#91). Only the V2 relay path carries these —
        // the raw Kalshi branch below is a venue aggregate with no lifetime
        // sums, so cards must treat them as optional, not assume zero.
        totalBoughtCount: pickNum(p.totalBoughtCount),
        totalSoldCount: pickNum(p.totalSoldCount),
        totalBuyCostCents: pickNum(p.totalBuyCostCents),
      };
    }

    // Kalshi /portfolio/positions market_positions[] carries the signed
    // contract count as `position_fp` (fp string — "1.00" / "-1.00") and
    // does not include an integer `position` field on current responses.
    // Without position_fp here, every row resolved to position 0 and got
    // filtered out — /positions showed "No open positions" despite live
    // contracts. Positive = long YES, negative = short YES (= long NO).
    const rawPos = pickNum(p.position, p.position_fp);
    let side: 'yes' | 'no';
    let position: number;
    if (rawPos !== undefined) { side = rawPos >= 0 ? 'yes' : 'no'; position = Math.abs(rawPos); }
    else { side = (p.side as 'yes' | 'no') ?? 'yes'; position = pickNum(p.quantity, p.total_traded) ?? 0; }
    if (position === 0) return null;
    // Kalshi also omits `average_price_dollars` on market_positions — derive
    // the avg from total_traded_dollars / |position| (correct for buy-only
    // open positions; reasonable approximation for mixed buy/sell cases).
    // Legacy field-name fallback retained for older payload shapes.
    const totalTradedDollars = pickNum(p.total_traded_dollars, p.total_traded);
    const avgFromTraded =
      totalTradedDollars != null && position > 0
        ? Math.round((totalTradedDollars / position) * 100)
        : null;
    const avgPrice =
      avgFromTraded ??
      dollarsToCents(
        p.average_price_dollars ?? p[`${side}_price_dollars`],
        p.average_price ?? p[`${side}_price`],
      );
    const realizedPnl = dollarsToCents(p.realized_pnl_dollars, p.realized_pnl);
    // Kalshi /portfolio/positions returns fees_paid_dollars (string, e.g.
    // "0.0734") with centicent precision. dollarsToCents Math.rounds to whole
    // cents, which would silently truncate sub-cent fees — so parse fees
    // directly and keep the fractional cents.
    const feesPaidCents =
      p.fees_paid_dollars !== undefined || p.fees_paid !== undefined
        ? parseFeesPaidCents(p.fees_paid_dollars, p.fees_paid)
        : undefined;
    return {
      ticker,
      side,
      position,
      averagePrice: avgPrice,
      lastFillAt: pickStr(p.lastFillAt, p.last_fill_at) || null,
      realizedPnl,
      feesPaidCents,
    };
  }).filter((p): p is KalshiPosition => p !== null);
}

function positionsEndpointFor(keyMode: 'org' | 'user' | null): string {
  return keyMode === 'org' ? '/api/trader/positions' : '/api/kalshi/positions';
}

// Org-scope fills come from the relay's persisted fills table (poller-resolved
// user_id), not the live Kalshi fetch — the latter filters by trades.kalshi_order_id
// which silently drops any fill whose order didn't get logged with a non-null
// kalshiOrderId, which has been masking recent fills entirely in org mode.
function fillsEndpointFor(keyMode: 'org' | 'user' | null): string {
  return keyMode === 'org' ? '/api/trader/fills' : '/api/kalshi/fills';
}

// Exported alongside parsePositions so org-wide surfaces reuse the same raw-
// Kalshi order parsing (remaining_count_fp, *_price_dollars) rather than a
// divergent hand-rolled copy.
export function parseOrders(data: Record<string, unknown>): KalshiOrder[] {
  const raw = (data.orders ?? data.results ?? data.data ?? []) as Record<string, unknown>[];
  if (!Array.isArray(raw)) return [];
  return raw.map((o): KalshiOrder => {
    // Reconstruct the trader-native {side, action} pair for DISPLAY. V2 is single-
    // book: a buy-NO is sent as an `ask` (sell YES), so it reads back as
    // side:"yes"/action:"sell" with outcome_side:"no". `outcome_side` is the
    // canonical trader side; we flip `action` when the order was placed cross-
    // framed so the pair stays coherent (a NO reads as {no, buy}, never {no, sell}).
    // Verified against live Kalshi reads — see reconstructOrderTraderSide.
    const { side, action } = reconstructOrderTraderSide(o);
    const reconciliation = (() => {
      const value = o.g1_order_reconciliation;
      if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
      const record = value as Record<string, unknown>;
      const actions = record.actions;
      if (!actions || typeof actions !== 'object' || Array.isArray(actions)) return undefined;
      const actionRecord = actions as Record<string, unknown>;
      if (
        record.source !== 'exact_order_detail' ||
        record.lifecycle !== 'resting_list_omitted' ||
        actionRecord.cancel !== 'available' ||
        actionRecord.decrease !== 'unavailable'
      ) return undefined;
      return {
        source: 'exact_order_detail' as const,
        lifecycle: 'resting_list_omitted' as const,
        verifiedAt: pickStr(record.verified_at) ?? '',
        marketStatus: pickStr(record.market_status) ?? null,
        marketStatusCheckedAt: pickStr(record.market_status_checked_at) ?? null,
        marketInactive:
          typeof record.market_inactive === 'boolean' ? record.market_inactive : null,
        actions: {
          cancel: 'available' as const,
          decrease: 'unavailable' as const,
        },
      };
    })();
    return {
      orderId: pickStr(o.order_id, o.id) ?? '',
      ticker: pickStr(o.ticker, o.market_ticker) ?? '',
      side,
      action,
      type: (o.type as 'limit' | 'market') ?? 'limit',
      status: pickStr(o.status) ?? 'resting',
      // Kalshi names the original order size `initial_count_fp` (a fixed-
      // point string, e.g. "1.00") — a resting order has no `count`/`count_fp`
      // field, so the legacy fallbacks always missed and count fell to 0.
      count:
        pickWholeContracts(o.initial_count, o.initial_count_fp, o.count, o.count_fp, o.quantity) ?? 0,
      remainingCount: pickWholeContracts(o.remaining_count, o.remaining_count_fp, o.remaining_quantity, o.quantity) ?? 0,
      fillCount: pickWholeContracts(o.fill_count, o.fill_count_fp),
      decreaseCount: pickWholeContracts(o.decrease_count, o.decrease_count_fp),
      yesPrice: dollarsToCents(o.yes_price_dollars, o.yes_price) || undefined,
      noPrice: dollarsToCents(o.no_price_dollars, o.no_price) || undefined,
      // Kalshi accepts `expiration_ts` (unix seconds) on order placement
      // but the orders LIST response surfaces the same value under
      // `expiration_time` as an ISO string. Earlier parser only checked
      // `expiration_ts` / `expires_at` so GTS orders read as GTC in
      // the Positions / Open Orders table. Try all the field shapes
      // we've seen in the wild and convert each to unix seconds.
      expirationTs: (() => {
        const num = pickNum(o.expiration_ts);
        if (num != null) return num;
        const isoCandidate =
          (typeof o.expiration_time === 'string' && o.expiration_time !== '')
            ? o.expiration_time
            : (typeof o.expires_at === 'string' && o.expires_at !== '')
              ? o.expires_at
              : null;
        if (isoCandidate) {
          const ms = new Date(isoCandidate).getTime();
          if (Number.isFinite(ms) && ms > 0) return Math.floor(ms / 1000);
        }
        return null;
      })(),
      createdTime: pickStr(o.created_time, o.created_at) ?? '',
      // Oscar blocker tagging rides on the venue echoing client_order_id on
      // reads (validated in the node1 smoke; relay-side enrichment from the
      // trades audit is the named fallback if it ever stops).
      clientOrderId: pickStr(o.client_order_id) || undefined,
      reconciliation,
    };
  });
}

/** Canonical relay orders envelope. Unlike parseOrders' compatibility parser,
 *  this validates transport shape before state can be replaced. A malformed
 *  successful REST/push body is a sync failure, never evidence of a flat book. */
export function parseOrdersSnapshot(data: unknown): KalshiOrder[] | null {
  if (!data || typeof data !== 'object') return null;
  const orders = (data as Record<string, unknown>).orders;
  if (!Array.isArray(orders)) return null;
  if (orders.some((order) => !order || typeof order !== 'object' || Array.isArray(order))) {
    return null;
  }
  const parsed = parseOrders({ orders });
  // #241: an ID-less row can't prove a target ID disappeared, so a non-empty
  // book containing one is not removal authority — treat the whole snapshot as
  // unreadable rather than let it retire a cancel fence. `{ orders: [] }` is
  // still a valid authoritative empty book (the #230/#238 canonical rule).
  if (parsed.some((order) => order.orderId === '')) return null;
  return parsed;
}

function logOpenOrdersEmptyTransition(
  source: 'rest' | 'push' | 'disconnect',
  previousCount: number,
): void {
  if (previousCount <= 0) return;
  const action = source === 'disconnect' ? 'cleared' : 'accepted non-empty→empty';
  console.warn(
    `[accountStore] ${new Date().toISOString()} source=${source} ${action} open-orders transition (previousOrders=${previousCount})`,
  );
}

export function parseFills(data: Record<string, unknown>): KalshiFill[] {
  const raw = (data.fills ?? data.results ?? data.data ?? []) as Record<string, unknown>[];
  if (!Array.isArray(raw)) return [];
  return raw.map((f): KalshiFill => {
    // Legacy `side` first (it pairs with `action` in the legacy convention the
    // P&L/position math uses). `outcome_side` flips on a sell fill, so preferring
    // it would mis-sign sell P&L and pick the wrong `${side}_price`. Fall back to
    // `outcome_side` only when legacy `side` is absent. Mirrors parseOrders.
    const side = ((f.side ?? f.outcome_side) as 'yes' | 'no') ?? 'yes';
    const price = dollarsToCents(f[`${side}_price_dollars`] ?? f.price_dollars, f[`${side}_price`] ?? f.price);
    return {
      fillId: pickStr(f.fill_id, f.trade_id, f.id) ?? `fill-${Date.now()}`,
      orderId: pickStr(f.order_id) ?? '',
      ticker: pickStr(f.ticker, f.market_ticker) ?? '',
      side,
      action: (f.action as 'buy' | 'sell') ?? 'buy',
      count: pickNum(f.count, f.count_fp, f.fill_count_fp, f.quantity) ?? 0,
      price,
      createdAt: pickStr(f.created_at, f.created_time) ?? new Date().toISOString(),
    };
  });
}

/** A fill row parseFills could only "parse" by inventing identity, price, size
 *  or time is not a fill — it is evidence the read is unreadable. Field names
 *  mirror parseFills' coalescing exactly so every shape it can faithfully parse
 *  is accepted, and nothing else. order_id stays optional (org-lane rows may
 *  predate kalshiOrderId capture). No Kalshi fill executes at 0¢, so a derived
 *  0 price always means the price fields were absent. */
function isUsableKalshiFillRow(f: Record<string, unknown>): boolean {
  const side = f.side ?? f.outcome_side;
  if (side !== 'yes' && side !== 'no') return false;
  if (f.action !== 'buy' && f.action !== 'sell') return false;
  if (pickStr(f.fill_id, f.trade_id, f.id) === undefined) return false;
  if (pickStr(f.ticker, f.market_ticker) === undefined) return false;
  const count = pickNum(f.count, f.count_fp, f.fill_count_fp, f.quantity);
  if (count === undefined || count <= 0) return false;
  const price = dollarsToCents(f[`${side}_price_dollars`] ?? f.price_dollars, f[`${side}_price`] ?? f.price);
  if (price <= 0) return false;
  const createdAt = pickStr(f.created_at, f.created_time);
  return createdAt !== undefined && Number.isFinite(Date.parse(createdAt));
}

/** Fail-closed fills read (#499). parseFills defaults every missing field, so a
 *  malformed row inside a valid array used to become a synthetic displayable
 *  fill — manufactured Recent Fills history. Mirrors parseOrdersSnapshot
 *  (#238/#241) and the Polymarket fills decoder: whole-payload rejection, not
 *  per-row filtering, because a partially dropped tape is indistinguishable
 *  from a complete one. Returns null for "could not read the fills", which is
 *  never "the trader has no fills"; `{ fills: [] }` remains the only
 *  authoritative empty read. */
export function parseFillsSnapshot(data: unknown): KalshiFill[] | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  const raw = d.fills ?? d.results ?? d.data;
  if (!Array.isArray(raw)) return null;
  for (const f of raw) {
    if (!f || typeof f !== 'object' || Array.isArray(f)) return null;
    if (!isUsableKalshiFillRow(f as Record<string, unknown>)) return null;
  }
  return parseFills({ fills: raw });
}

type OrdersSnapshotRead =
  | { ok: true; orders: KalshiOrder[] }
  | { ok: false; error: string };

/** Read an open-orders response under ONE transport contract: a non-2xx, a body
 *  that isn't JSON, and a malformed successful envelope all mean "could not read
 *  the book", never "the book is flat". Returns a result rather than throwing so
 *  an orders-only fault degrades the orders surface instead of failing connect.
 *
 *  #238 introduced this for the bootstrap; #241 routes refreshOrders through it
 *  too, because the poll path's permissive parser mapped a malformed 200 onto []
 *  — which would have silently retired every pending cancel fence. */
async function readOrdersSnapshotResponse(res: Response): Promise<OrdersSnapshotRead> {
  if (!res.ok) return { ok: false, error: `HTTP ${res.status}` };
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    return { ok: false, error: 'malformed orders payload' };
  }
  const orders = parseOrdersSnapshot(body);
  if (orders === null) return { ok: false, error: 'malformed orders payload' };
  return { ok: true, orders };
}

export const useAccountStore = create<AccountState>((set, get) => ({
  status: 'disconnected',
  error: null,
  ordersSync: { lastSuccessMs: null, lastErrorMs: null, lastError: null, consecutiveFailures: 0 },
  keyHealth: [],
  credentialCount: 0,
  keyMode: null,
  balance: null,
  positions: [],
  openOrders: [],
  kalshiCancelStates: {},
  recentFills: [],
  activityTicker: [],
  fillsSynced: false,
  realizedPnlCents: 0,
  realizedFeesCents: null,
  realizedPnlNetCents: null,
  openNotionalCents: 0,

  checkCredentials: async () => {
    try {
      const res = await apiFetch('/api/trader/key-mode');
      if (!res.ok) { set({ credentialCount: 0 }); return; }
      const data = await res.json() as { canTrade?: boolean; mode?: 'org' | 'user' };
      const canTrade = data.canTrade === true;
      const keyMode: 'org' | 'user' | null = data.mode === 'org' || data.mode === 'user' ? data.mode : null;
      set({ credentialCount: canTrade ? 1 : 0, keyMode });
      const s = get().status;
      if (canTrade && (s === 'disconnected' || s === 'error')) {
        get().connect();
      }
    } catch {
      set({ credentialCount: 0 });
    }
  },

  connect: async () => {
    // We want a live connection from here until disconnect() — arm the
    // self-heal watchdog (idempotent) so a stale-generation strand can't leave
    // the session wedged at 'connecting' forever. Set before the in-flight
    // guard so even a swallowed concurrent call registers the desire.
    if (!_desiredConnected) _fillSoundSessionStartedAtMs = Date.now();
    _desiredConnected = true;
    ensureHealWatchdog(get);
    // Concurrency guard MUST be an in-flight flag, not a status check: the
    // retry ladder sets status 'connecting' before scheduling its timer, so
    // a status-based guard made every scheduled retry early-return — after
    // ONE transient failure the store wedged at 'connecting' forever
    // ("retrying in 3s (1/5)" that never retried), polling stopped, and the
    // session sat silently dead until a manual reload. That wedge is the
    // probable mechanism behind the recurring morning "orders invisible"
    // incidents (review finding, Jun 11).
    if (_connectInFlight) return;
    _connectInFlight = true;
    const generation = nextConnectionGeneration();
    if (_connectRetryTimer) { clearTimeout(_connectRetryTimer); _connectRetryTimer = null; }
    set({ status: 'connecting', error: null });

    try {
      const modeRes = await apiFetch('/api/trader/key-mode');
      if (!modeRes.ok) {
        const errData = await modeRes.json().catch(() => ({}));
        throw new Error((errData as Record<string, string>).error ?? 'Failed to load key mode');
      }
      const modeData = await modeRes.json() as { canTrade?: boolean; mode?: 'org' | 'user' };
      if (!isCurrentConnectionGeneration(generation)) return;
      const canTrade = modeData.canTrade === true;
      const keyMode: 'org' | 'user' = modeData.mode === 'org' ? 'org' : 'user';
      set({ credentialCount: canTrade ? 1 : 0, keyMode });

      if (!canTrade) {
        if (!isCurrentConnectionGeneration(generation)) return;
        _connectRetryAttempts = 0;
        set({ status: 'disconnected', error: 'No credentials available. Ask your manager to add org keys, or add personal keys in Settings.' });
        return;
      }

      // Balance probe. Two very different failure semantics by key mode:
      //  - PERSONAL: this is the auth check for a key the user just typed —
      //    a failure must fail the connect loudly (April-era behavior kept).
      //  - ORG: creds are managed server-side and already validated; a
      //    failure here means KALSHI is down (e.g. the Thursday 3–5am ET
      //    maintenance pause — docs.kalshi.com "Maintenance and Pauses"),
      //    and a venue outage must DEGRADE the Kalshi surfaces, not block
      //    the whole dash: Polymarket doesn't need Kalshi at all. Before
      //    the Jul-9 balance unmask, org traders never even hit Kalshi on
      //    this path (placeholder), so fatal-on-org would be a NEW
      //    outage coupling; managers/admins gain the resilience they never
      //    had. Balance stays null in the degraded state (UI shows '—').
      const balRes = await apiFetch('/api/kalshi/balance').catch(() => null);
      if (!isCurrentConnectionGeneration(generation)) return;
      let balance: number | null = null;
      if (balRes?.ok) {
        const balData = await balRes.json() as Record<string, unknown>;
        if (!isCurrentConnectionGeneration(generation)) return;
        // Org-keys mode returns the REAL org-account balance (relay
        // unmasked Jul-9 — balance is account-level, no per-trader slice
        // exists). Null only when the fields are genuinely absent (e.g. an
        // old relay still serving the placeholder) so the UI hides the
        // value instead of showing "$0.00".
        balance = readKalshiReportedBalanceCents(balData);
      } else if (keyMode !== 'org') {
        const errData = balRes ? await balRes.json().catch(() => ({})) : {};
        throw new Error((errData as Record<string, string>).error ?? 'Failed to fetch balance');
      }

      const [posRes, ordRes, pnlRes] = await Promise.all([
        apiFetch(positionsEndpointFor(keyMode)),
        apiFetch('/api/kalshi/orders'),
        keyMode === 'org' ? apiFetch('/api/trader/pnl') : Promise.resolve(null),
      ]);
      if (!isCurrentConnectionGeneration(generation)) return;

      const positions = posRes.ok ? parsePositions(await posRes.json()) : [];
      // Orders fail CLOSED here (#238 / Danielle DNL-001 on #230). This path
      // runs on every RECONNECT, and mapping a 502 or a malformed 200 onto []
      // replaced a live resting book with an empty list while stamping a fresh
      // ordersSync success — the trader saw "No open orders" beside a healthy
      // sync clock (the #230 disappearing-orders class). refreshOrders already
      // keeps last-good when the transport fails; bootstrap did not, and now
      // reads under the same contract.
      const ordersRead = await readOrdersSnapshotResponse(ordRes);
      const pnl = pnlRes && pnlRes.ok
        ? await pnlRes.json() as {
            realizedPnlCents?: number;
            realizedFeesCents?: number | null;
            realizedPnlNetCents?: number | null;
            openNotionalCents?: number;
          }
        : null;
      if (!isCurrentConnectionGeneration(generation)) return;

      _connectRetryAttempts = 0;
      const prevSync = get().ordersSync;
      if (!ordersRead.ok) {
        console.warn(
          `[accountStore] open-orders bootstrap failed (${prevSync.consecutiveFailures + 1}x): ${ordersRead.error}`,
        );
      }
      set({
        status: 'connected', error: null, balance,
        positions,
        // openOrders is written ONLY on a snapshot we could actually read.
        // Omitting the key (rather than writing the old value back) preserves
        // the retained list by identity, so nothing downstream re-renders on a
        // failed read.
        ...(ordersRead.ok
          ? {
              openOrders: reconcilePlacementConfirmations(ordersRead.orders),
              kalshiCancelStates: reconcileCancelFence(get().kalshiCancelStates, ordersRead.orders),
            }
          : null),
        // On a good read the bootstrap has a fresh snapshot — stamp the sync
        // clock so the staleness badge doesn't flash a pre-reconnect age. On a
        // failed read, keep the prior lastSuccessMs: a false success would hide
        // the staleness badge over an unreadable book.
        ordersSync: ordersRead.ok
          ? { lastSuccessMs: Date.now(), lastErrorMs: null, lastError: null, consecutiveFailures: 0 }
          : {
              ...prevSync,
              lastErrorMs: Date.now(),
              lastError: ordersRead.error,
              consecutiveFailures: prevSync.consecutiveFailures + 1,
            },
        realizedPnlCents: pnl?.realizedPnlCents ?? 0,
        realizedFeesCents: pnl?.realizedFeesCents ?? null,
        realizedPnlNetCents: pnl?.realizedPnlNetCents ?? null,
        openNotionalCents: pnl?.openNotionalCents ?? 0,
      });

      startPolling(() => {
        const s = get().status;
        if (!isCurrentConnectionGeneration(generation) || s !== 'connected') {
          stopPolling();
          return;
        }

        apiFetch('/api/kalshi/balance').then(async (r) => {
          if (r.ok) {
            const d = await r.json() as Record<string, unknown>;
            if (!isCurrentConnectionGeneration(generation) || get().status !== 'connected') return;
            // Org scope now carries the real account balance too (relay
            // unmasked Jul-8); an old relay's placeholder has no balance
            // fields and leaves the stored value untouched.
            set({ balance: pickNum(d.balance, d.available_balance) ?? get().balance });
          }
        }).catch(() => {});

        get().refreshPositions().catch(() => {});
        get().refreshOrders().catch(() => {});
        get().refreshPnl().catch(() => {});
        get().refreshFills().catch(() => {});
      });
    } catch (err) {
      if (!isCurrentConnectionGeneration(generation)) return;
      const message = err instanceof Error ? err.message : 'Connection failed';
      stopPolling();
      if (_connectRetryAttempts < MAX_CONNECT_RETRIES) {
        const delay = BASE_CONNECT_RETRY_MS * 2 ** _connectRetryAttempts;
        _connectRetryAttempts++;
        set({ status: 'connecting', error: `${message} — retrying in ${Math.round(delay / 1000)}s (${_connectRetryAttempts}/${MAX_CONNECT_RETRIES})` });
        _connectRetryTimer = setTimeout(() => {
          if (!isCurrentConnectionGeneration(generation)) return;
          void get().connect();
        }, delay);
      } else {
        _connectRetryAttempts = 0;
        set({ status: 'error', error: `${message} — auto-retrying every 60s` });
        // Terminal 'error' used to be PERMANENT until a manual reload —
        // morning transients left traders with a dead, silently-frozen
        // session (orders list empty, no recovery). Keep nudging gently.
        _connectRetryTimer = setTimeout(() => {
          if (!isCurrentConnectionGeneration(generation)) return;
          if (get().status !== 'error') return;
          void get().connect();
        }, SLOW_CONNECT_RETRY_MS);
      }
    } finally {
      _connectInFlight = false;
    }
  },

  disconnect: () => {
    // Connection is no longer wanted — silence the self-heal watchdog so it
    // doesn't fight a deliberate disconnect/logout, and tear down its interval
    // so a logged-out tab leaves no lingering timer. connect() re-arms it (it
    // calls ensureHealWatchdog before the in-flight guard), so a swallowed
    // remount-reconnect still gets watchdog coverage.
    _desiredConnected = false;
    if (_healWatchdog) { clearInterval(_healWatchdog); _healWatchdog = null; }
    _connectionGeneration += 1;
    stopPolling();
    if (_connectRetryTimer) { clearTimeout(_connectRetryTimer); _connectRetryTimer = null; }
    _connectRetryAttempts = 0;
    _seenFillIds.clear();
    _seenFillOrder.length = 0;
    _seenOrderIds.clear();
    logOpenOrdersEmptyTransition('disconnect', get().openOrders.length);
    // #241: drop every cancel fence and its timer. A logged-out tab must not
    // keep a timeout armed, and a reconnected session starts unfenced.
    forgetAllCancelAttempts();
    forgetAllPlacementConfirmations();
    set({
      status: 'disconnected',
      error: null,
      kalshiCancelStates: {},
      ordersSync: { lastSuccessMs: null, lastErrorMs: null, lastError: null, consecutiveFailures: 0 },
      keyHealth: [],
      keyMode: null,
      balance: null,
      positions: [],
      openOrders: [],
      recentFills: [],
      activityTicker: [],
      fillsSynced: false,
      realizedPnlCents: 0,
      realizedFeesCents: null,
      realizedPnlNetCents: null,
      openNotionalCents: 0,
    });
  },

  refreshPositions: async () => {
    const generation = _connectionGeneration;
    if (get().status !== 'connected') return false;
    try {
      const res = await apiFetch(positionsEndpointFor(get().keyMode));
      if (!res.ok) return false;
      const positions = parsePositions(await res.json());
      if (!isCurrentConnectionGeneration(generation) || get().status !== 'connected') return false;
      set({ positions });
      return true;
    } catch {
      return false;
    }
  },

  refreshPnl: async () => {
    const generation = _connectionGeneration;
    if (get().status !== 'connected' || get().keyMode !== 'org') return;
    try {
      const res = await apiFetch('/api/trader/pnl');
      if (!res.ok) return;
      const pnl = await res.json() as {
        realizedPnlCents?: number;
        realizedFeesCents?: number | null;
        realizedPnlNetCents?: number | null;
        openNotionalCents?: number;
      };
      if (!isCurrentConnectionGeneration(generation) || get().status !== 'connected') return;
      set({
        realizedPnlCents: pnl.realizedPnlCents ?? 0,
        realizedFeesCents: pnl.realizedFeesCents ?? null,
        realizedPnlNetCents: pnl.realizedPnlNetCents ?? null,
        openNotionalCents: pnl.openNotionalCents ?? 0,
      });
    } catch {}
  },

  refreshFills: async () => {
    const generation = _connectionGeneration;
    if (get().status !== 'connected') return;
    try {
      const res = await apiFetch(fillsEndpointFor(get().keyMode));
      if (!res.ok) return;
      // Fail-closed (#499): a malformed body/row is "could not read the fills",
      // so it lands nothing and — critically — grants no empty-history
      // authority. Only a validated snapshot (including a validated []) latches
      // fillsSynced, the one signal ActivityTicker may claim emptiness from.
      const fills = parseFillsSnapshot(await res.json());
      if (fills === null) return;
      if (!isCurrentConnectionGeneration(generation) || get().status !== 'connected') return;
      for (const fill of fills) get().processFill(fill);
      set({ fillsSynced: true });
    } catch {}
  },

  refreshOrders: async () => {
    const generation = _connectionGeneration;
    if (get().status === 'disconnected') return;
    const recordFailure = (reason: string) => {
      if (!isCurrentConnectionGeneration(generation)) return;
      const prev = get().ordersSync;
      const failures = prev.consecutiveFailures + 1;
      // Keep the stale list (better than blanking mid-session) but make the
      // failure VISIBLE: state for the UI badge, console for diagnostics.
      console.warn(
        `[accountStore] ${new Date().toISOString()} source=rest open-orders refresh failed (${failures}x): ${reason}`,
      );
      set({
        ordersSync: {
          ...prev,
          lastErrorMs: Date.now(),
          lastError: reason,
          consecutiveFailures: failures,
        },
      });
    };
    try {
      const res = await apiFetch('/api/kalshi/orders');
      // Same fail-closed transport contract the bootstrap uses (#238), so a
      // malformed 200 records a sync failure and keeps the last-good list
      // instead of masquerading as a flat book (#241).
      const read = await readOrdersSnapshotResponse(res);
      if (!read.ok) {
        recordFailure(read.error);
        return;
      }
      const orders = reconcilePlacementConfirmations(read.orders);
      if (!isCurrentConnectionGeneration(generation) || get().status === 'disconnected') return;
      if (orders.length === 0) {
        logOpenOrdersEmptyTransition('rest', get().openOrders.length);
      }
      set({
        openOrders: orders,
        kalshiCancelStates: reconcileCancelFence(get().kalshiCancelStates, orders),
        ordersSync: {
          lastSuccessMs: Date.now(),
          lastErrorMs: get().ordersSync.lastErrorMs,
          lastError: null,
          consecutiveFailures: 0,
        },
      });
    } catch (err) {
      recordFailure(err instanceof Error ? err.message : 'network error');
    }
  },

  processFill: (fill: KalshiFill) => {
    if (_seenFillIds.has(fill.fillId)) return;
    _seenFillIds.add(fill.fillId);
    _seenFillOrder.push(fill.fillId);
    while (_seenFillIds.size > MAX_SEEN_FILLS) {
      const oldest = _seenFillOrder.shift();
      if (oldest) _seenFillIds.delete(oldest);
    }

    const recentFills = [fill, ...get().recentFills].slice(0, MAX_RECENT_FILLS);
    const activityTicker = recentFills.slice(0, RECENT_FILL_LIMIT);
    const confirmation = _placementConfirmations.get(fill.orderId);
    if (confirmation) {
      const remainingCount = Math.max(0, confirmation.order.remainingCount - fill.count);
      if (remainingCount === 0) {
        forgetPlacementConfirmation(fill.orderId);
        set({
          recentFills,
          activityTicker,
          openOrders: get().openOrders.filter((order) => order.orderId !== fill.orderId),
        });
      } else {
        const confirmedOrder = { ...confirmation.order, remainingCount };
        _placementConfirmations.set(fill.orderId, {
          ...confirmation,
          order: confirmedOrder,
        });
        set({
          recentFills,
          activityTicker,
          openOrders: get().openOrders.map((order) =>
            order.orderId === fill.orderId ? confirmedOrder : order,
          ),
        });
      }
    } else {
      set({ recentFills, activityTicker });
    }

    const fillCreatedAtMs = new Date(fill.createdAt).getTime();
    const fillAgeMs = Date.now() - fillCreatedAtMs;
    const belongsToCurrentSoundSession =
      _fillSoundSessionStartedAtMs === null || fillCreatedAtMs >= _fillSoundSessionStartedAtMs;
    const isRecent =
      belongsToCurrentSoundSession && fillAgeMs >= 0 && fillAgeMs < FILL_SOUND_RECENCY_MS;
    const snd = readSoundSettings();
    if (isRecent && snd.tradeSoundEnabled) {
      // First-fill status is consumed even below the threshold (#189): the
      // sub-threshold first fill IS the ka-ching, so a later big fill must
      // not replay it.
      const isFirstFill = !_seenOrderIds.has(fill.orderId);
      _seenOrderIds.add(fill.orderId);

      const isFirstFillAlert = isFirstFill && snd.firstSaleSoundEnabled;
      if (isFirstFillAlert || fill.count >= snd.minFillContracts) {
        // Per-alert volume (#232): the channel whose tone this is decides its
        // own multiplier. Read INSIDE the closure, not here — a pending sound
        // can sit in the unlock queue across a settings change, and the volume
        // that matters is the one in force when it actually sounds.
        const channel: AlertChannelId =
          fill.count >= 1000 && snd.whaleSoundEnabled ? 'whaleFill'
          : isFirstFillAlert ? 'firstFill'
          : 'fill';
        const tone = channel === 'whaleFill' ? playWhaleTone
          : channel === 'firstFill' ? playKaChingTone
          : playFillTone;
        const playFn = () => tone(getChannelVolume(channel));

        const ctx = getAudioContext();
        if (ctx?.state === 'running') { playFn(); }
        else { setPendingSound(playFn); }
      }
    }

    get().refreshPositions().catch(() => {});
  },

  placeOrder: async (input: OrderInput) => {
    try {
      const order = await postOrderRaw(input);

      const placed: KalshiOrder = {
        orderId: pickStr(order.order_id, order.id) ?? '',
        ticker: input.ticker,
        side: input.side,
        action: input.action,
        type: input.type,
        status: pickStr(order.status) ?? 'resting',
        count: input.count,
        remainingCount: pickWholeContracts(order.remaining_count, order.remaining_count_fp) ?? input.count,
        yesPrice: input.side === 'yes' ? input.price : undefined,
        noPrice: input.side === 'no' ? input.price : undefined,
        createdTime: pickStr(order.created_time) ?? new Date().toISOString(),
      };

      // Project only explicit, fully identifiable RESTING evidence. Missing
      // status/remaining fields, immediate fills, rejects and market orders do
      // not enter the fence and therefore cannot mint a phantom open order.
      const rawStatus = pickStr(order.status);
      const rawRemaining = pickWholeContracts(order.remaining_count, order.remaining_count_fp);
      if (placed.orderId && rawStatus === 'resting' && rawRemaining != null && rawRemaining > 0) {
        _placementConfirmations.set(placed.orderId, {
          order: placed,
          expiresAtMs: Date.now() + KALSHI_PLACEMENT_CONFIRMATION_TIMEOUT_MS,
        });
        set({
          openOrders: [
            ...get().openOrders.filter((candidate) => candidate.orderId !== placed.orderId),
            placed,
          ],
        });
      }

      const snd = readSoundSettings();
      if (snd.orderPlacedSoundEnabled) playOrderPlacedTone(getChannelVolume('orderPlaced'));

      get().refreshOrders().catch(() => {});

      return placed;
    } catch (err) {
      const snd = readSoundSettings();
      if (snd.orderRejectionSoundEnabled) playOrderRejectedTone(getChannelVolume('orderRejected'));
      throw err;
    }
  },

  placeOrderSequence: async (input: OscarSequenceInput) => {
    let result: OscarSequenceResult;
    try {
      // Same boundary as postOrderRaw: retain exchangeIndex in the local Oscar
      // object/retry identity, but do not send it as routing authority. #575
      // owns the relay wire change.
      const { exchangeIndex: _observedExchangeIndex, ...wireInput } = input;
      const res = await apiFetch('/api/kalshi/order-sequence', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(wireInput),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        if (isLimitRejectionPayload(errData)) {
          throw new LimitRejectionError(errData.error, errData.code, errData.breach);
        }
        throw new Error(
          (errData as Record<string, string>).error ?? `Sequence failed (${res.status})`,
        );
      }
      result = (await res.json()) as OscarSequenceResult;
    } catch (err) {
      const snd = readSoundSettings();
      if (snd.orderRejectionSoundEnabled) playOrderRejectedTone(getChannelVolume('orderRejected'));
      throw err;
    }
    // 200 with per-leg statuses: tone on the overall outcome (a partial —
    // e.g. take landed, blocker failed — is a rejection-tone event; the
    // ticket renders the leg detail).
    const snd = readSoundSettings();
    if (result.ok) {
      if (snd.orderPlacedSoundEnabled) playOrderPlacedTone(getChannelVolume('orderPlaced'));
    } else if (snd.orderRejectionSoundEnabled) {
      playOrderRejectedTone(getChannelVolume('orderRejected'));
    }
    get().refreshOrders().catch(() => {});
    return result;
  },

  sweepCloseLeg: async (ticker, side, quantity) => {
    let remaining = quantity;
    let residual: number | null = null;
    let placedTone = false;
    let sweepError: unknown = null;
    try {
      for (let attempt = 0; attempt < MAX_CLOSE_ATTEMPTS; attempt++) {
        // Marketable IOC at the 1¢ slippage floor — "sell into any bid the
        // book has". reduce_only so a stale count can't over-fill past flat
        // into the opposite side at the book extreme.
        const order = await postOrderRaw({
          ticker,
          side,
          action: 'sell',
          type: 'market',
          count: remaining,
          price: 1,
          reduceOnly: true,
        });
        if (!placedTone) {
          placedTone = true;
          const snd = readSoundSettings();
          if (snd.orderPlacedSoundEnabled) playOrderPlacedTone(getChannelVolume('orderPlaced'));
        }
        residual = residualOpenContracts(order, remaining);
        // Unverifiable (no fill counts on the response) — stop, don't blind-retry.
        if (residual === null) break;
        if (residual <= 0) {
          residual = 0;
          break;
        }
        // Partial fill: re-sweep ONLY the contracts still open.
        remaining = residual;
      }
    } catch (err) {
      // Don't decide success/failure yet. A reduce_only IOC fired at an already
      // flat leg — a routine close-time race (a concurrent fill closed the last
      // lot), or a reduce-to-zero reject Kalshi doesn't document — can throw on a
      // leg that is actually CLOSED. Settle it against the live position below
      // instead of blindly reporting a failure on a position that's already flat.
      sweepError = err;
    }

    // Read the TRUE open count from the live position — the same source the desk
    // UI trusts — but ONLY when refreshPositions FRESHLY succeeded this call
    // (connected, same connection generation, HTTP 200). A no-op/stale/empty
    // positions array must NEVER be read as proof of a flat leg: a disconnect mid
    // close could otherwise clamp a real residual to 0 and report a close as done
    // while contracts stay open. liveOpen === null means "couldn't confirm".
    // Reconcile when the IOC response leaves real-money ambiguity: a thrown sweep
    // (leg may already be flat), an opaque/null response, or a surviving residual.
    const needsLiveTruth = sweepError !== null || residual === null || residual > 0;
    let liveOpen: number | null = null;
    if (needsLiveTruth) {
      try {
        const fresh = await get().refreshPositions();
        if (fresh && get().status === 'connected') {
          const live = get().positions.find((p) => p.ticker === ticker && p.side === side);
          if (live) {
            liveOpen = Math.max(0, Math.round(live.position));
          } else {
            // Leg ABSENT from the positions read. In USER mode (and the null
            // pre-mode) that read is live Kalshi /portfolio/positions — paged to
            // exhaustion, authoritative — so absent means genuinely flat. In ORG
            // mode it's a reconstruction from the relay's fills ledger, which the
            // poller lags up to ~10s behind Kalshi; absent there can mean "fills
            // not ingested yet", NOT positively flat. Never read org-ledger absence
            // as proof of flat — leave liveOpen null ("couldn't confirm") so the
            // close warns instead of false-flatting hidden exposure on the gbuff
            // market-making lane.
            liveOpen = get().keyMode === 'org' ? null : 0;
          }
        }
      } catch {
        liveOpen = null;
      }
    }

    if (sweepError !== null) {
      // Suppress the error ONLY when the live position POSITIVELY confirms the leg
      // is already flat (the throw was a no-op on a closed leg). Otherwise the
      // failure is real or unconfirmable — surface it loudly, never as success.
      if (liveOpen === 0) {
        get().refreshOrders().catch(() => {});
        return 0;
      }
      const snd = readSoundSettings();
      if (snd.orderRejectionSoundEnabled) playOrderRejectedTone(getChannelVolume('orderRejected'));
      throw sweepError;
    }

    if (residual === null && liveOpen !== null) {
      // Opaque response, but a trustworthy live read resolved the leg: adopt it —
      // flat (0) → success, open (>0) → INCOMPLETE — instead of nagging UNVERIFIED
      // while the desk already shows the leg closed. An untrustworthy read leaves
      // liveOpen null → residual stays null → UNVERIFIED survives (no false flat).
      residual = liveOpen;
    } else if (residual !== null && residual > 0 && liveOpen !== null) {
      // Clamp a surviving residual to the trustworthy live count — kills a phantom
      // residual when reduce_only capped the last IOC below `remaining` (a stale
      // snapshot vs the true open count). If the read wasn't trustworthy (liveOpen
      // null), KEEP the response-derived residual so the close warns rather than
      // falsely reporting flat.
      residual = Math.min(residual, liveOpen);
    }
    get().refreshOrders().catch(() => {});
    return residual;
  },

  cancelOrders: async (orderIds, options) => {
    // #241's state machine, batched (#272). The ONLY structural difference from
    // the single-order path is the claim: every ID in the batch is fenced in one
    // synchronous `set` before the first DELETE goes out. Claiming lazily, per
    // iteration, would leave every not-yet-reached ID clickable while the batch
    // ran — a single-row click would then send a duplicate DELETE for an order
    // the sweep is about to cancel, which is exactly the #241 defect at scale.
    const refreshAfter = options?.refreshAfter !== false;

    const unique: string[] = [];
    const seenIds = new Set<string>();
    for (const raw of orderIds) {
      const orderId = typeof raw === 'string' ? raw.trim() : '';
      if (!orderId || seenIds.has(orderId)) continue;
      seenIds.add(orderId);
      unique.push(orderId);
    }
    if (unique.length === 0) return [];

    const results = new Map<string, KalshiCancelBatchResult>();
    const claimed: Array<{ orderId: string; token: number }> = [];

    // ── Claim phase: no awaits between here and the `set`. ──
    let states = get().kalshiCancelStates;
    const claimedAtMs = Date.now();
    for (const orderId of unique) {
      if (isKalshiCancelPending(states[orderId])) {
        // Another surface (or an earlier batch) already owns this ID. Report it
        // rather than resending — a retry report must never resend a submitted
        // cancel.
        results.set(orderId, {
          orderId,
          status: 'already-pending',
          error: CANCEL_ALREADY_PENDING_MESSAGE,
        });
        continue;
      }
      const token = ++_cancelAttemptSeq;
      _cancelAttempts.set(orderId, token);
      clearCancelTimer(orderId); // a retry supersedes any timed-out attempt's timer
      states = beginKalshiCancel(states, orderId, claimedAtMs);
      claimed.push({ orderId, token });
    }
    if (claimed.length > 0) set({ kalshiCancelStates: states });

    try {
      // Sequential within the venue: the relay's Kalshi lane is a shared token
      // bucket, and a burst of parallel DELETEs buys nothing but rate-limit risk.
      for (const { orderId, token } of claimed) {
        try {
          await deleteKalshiOrder(orderId);
          forgetPlacementConfirmation(orderId);
        } catch (err) {
          if (_cancelAttempts.get(orderId) === token) {
            forgetCancelAttempt(orderId);
            set({ kalshiCancelStates: clearKalshiCancel(get().kalshiCancelStates, orderId) });
          }
          results.set(orderId, {
            orderId,
            status: 'failed',
            error: err instanceof Error && err.message ? err.message : 'Cancel failed',
          });
          continue;
        }

        // 2xx means SUBMITTED, never removed (#241). The fence stays up until an
        // authoritative snapshot drops the ID.
        results.set(orderId, { orderId, status: 'submitted', error: null });

        // A newer attempt, a disconnect, or an authoritative snapshot may have
        // retired this attempt while the DELETE was in flight — never re-fence.
        if (_cancelAttempts.get(orderId) !== token) continue;
        const current = get().kalshiCancelStates;
        const awaiting = markKalshiCancelAwaitingRemoval(current, orderId, Date.now());
        if (awaiting === current) continue;
        set({ kalshiCancelStates: awaiting });
        armCancelSyncTimer(orderId, token);
      }
    } finally {
      // ONE refresh for the whole batch. `refreshAfter: false` hands that duty to
      // a mixed-venue caller that refreshes each venue exactly once itself.
      if (refreshAfter && claimed.length > 0) get().refreshOrders().catch(() => {});
    }

    return unique.map(
      (orderId) =>
        results.get(orderId) ?? { orderId, status: 'failed' as const, error: 'Cancel not attempted' },
    );
  },

  cancelOrdersByTickers: async (tickers) => {
    const tickerSet = new Set(tickers.map((t) => t.trim()).filter(Boolean));
    if (tickerSet.size === 0) return [];

    // Locally-known targets — same claim/fence as cancelOrders so their rows
    // disable immediately and the #241 fence holds while the relay works.
    // The relay derives its OWN target set; ids it finds beyond ours (a
    // staler local list) come back in the results and simply need no local
    // fence release.
    const localIds: string[] = [];
    for (const o of get().openOrders) {
      if (!tickerSet.has(o.ticker)) continue;
      if (o.remainingCount <= 0) continue;
      localIds.push(o.orderId);
    }

    const results = new Map<string, KalshiCancelBatchResult>();
    const claimed: Array<{ orderId: string; token: number }> = [];
    let states = get().kalshiCancelStates;
    const claimedAtMs = Date.now();
    for (const orderId of localIds) {
      if (isKalshiCancelPending(states[orderId])) {
        results.set(orderId, {
          orderId,
          status: 'already-pending',
          error: CANCEL_ALREADY_PENDING_MESSAGE,
        });
        continue;
      }
      const token = ++_cancelAttemptSeq;
      _cancelAttempts.set(orderId, token);
      clearCancelTimer(orderId);
      states = beginKalshiCancel(states, orderId, claimedAtMs);
      claimed.push({ orderId, token });
    }
    if (claimed.length > 0) set({ kalshiCancelStates: states });

    const releaseClaims = () => {
      let st = get().kalshiCancelStates;
      for (const { orderId, token } of claimed) {
        if (_cancelAttempts.get(orderId) !== token) continue;
        forgetCancelAttempt(orderId);
        st = clearKalshiCancel(st, orderId);
      }
      set({ kalshiCancelStates: st });
    };

    try {
      let res: Response;
      try {
        res = await apiFetch('/api/kalshi/orders/cancel-sequence', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tickers: [...tickerSet] }),
        });
      } catch {
        // Network failure before the relay answered: nothing was submitted —
        // release and fall back to the sequential path (which retries per-id).
        releaseClaims();
        return get().cancelOrders(localIds);
      }
      if (res.status === 404) {
        // Older relay without the route — byte-identical old behavior.
        releaseClaims();
        return get().cancelOrders(localIds);
      }
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        const message =
          (errData as Record<string, string>).error ?? `Pull failed (${res.status})`;
        releaseClaims();
        for (const { orderId } of claimed) {
          results.set(orderId, { orderId, status: 'failed', error: message });
        }
        return localIds.map(
          (orderId) =>
            results.get(orderId) ?? { orderId, status: 'failed' as const, error: message },
        );
      }

      const payload = (await res.json().catch(() => null)) as {
        results?: Array<{ orderId?: unknown; status?: unknown; error?: unknown }>;
      } | null;
      const relayResults = Array.isArray(payload?.results) ? payload.results : [];
      const byId = new Map<string, { status: string; error: string | null }>();
      for (const r of relayResults) {
        if (typeof r?.orderId !== 'string' || typeof r?.status !== 'string') continue;
        byId.set(r.orderId, {
          status: r.status,
          error: typeof r.error === 'string' ? r.error : null,
        });
      }

      for (const { orderId, token } of claimed) {
        const relay = byId.get(orderId);
        if (relay == null || relay.status === 'canceled' || relay.status === 'already_gone') {
          // Venue-confirmed gone (or the relay's fresher lookup no longer
          // listed it — same end state). Fence moves to awaiting-removal
          // until an authoritative snapshot drops the id, exactly like a
          // submitted DELETE.
          forgetPlacementConfirmation(orderId);
          results.set(orderId, { orderId, status: 'submitted', error: null });
          if (_cancelAttempts.get(orderId) !== token) continue;
          const current = get().kalshiCancelStates;
          const awaiting = markKalshiCancelAwaitingRemoval(current, orderId, Date.now());
          if (awaiting === current) continue;
          set({ kalshiCancelStates: awaiting });
          armCancelSyncTimer(orderId, token);
        } else {
          if (_cancelAttempts.get(orderId) === token) {
            forgetCancelAttempt(orderId);
            set({ kalshiCancelStates: clearKalshiCancel(get().kalshiCancelStates, orderId) });
          }
          results.set(orderId, {
            orderId,
            status: 'failed',
            error: relay.error ?? 'Cancel failed',
          });
        }
      }
    } finally {
      if (claimed.length > 0 || tickerSet.size > 0) get().refreshOrders().catch(() => {});
    }

    return localIds.map(
      (orderId) =>
        results.get(orderId) ?? { orderId, status: 'failed' as const, error: 'Cancel not attempted' },
    );
  },

  cancelOrder: async (orderId: string) => {
    // #241 — the user-facing single cancel. Delegates to the batch so there is
    // exactly ONE fenced cancel path in the app; the throwing contract callers
    // depend on is reconstructed from the batch's per-ID result.
    // replaceOrder deliberately bypasses all of this via requestKalshiCancel.
    const [result] = await get().cancelOrders([orderId]);
    if (!result) return;
    if (result.status === 'already-pending') throw new Error(CANCEL_ALREADY_PENDING_MESSAGE);
    if (result.status === 'failed') throw new Error(result.error ?? 'Cancel failed');
  },

  decreaseOrder: async (orderId: string, nextQuantity: number) => {
    if (!Number.isFinite(nextQuantity) || !Number.isInteger(nextQuantity) || nextQuantity < 1) {
      throw new Error('Decrease target must be a positive integer ≥ 1.');
    }
    const existing = get().openOrders.find((order) => order.orderId === orderId);
    if (existing && !canEditKalshiOrder(existing)) {
      throw new Error('This recovered order can be cancelled, but it cannot be edited safely.');
    }
    const res = await apiFetch(`/api/kalshi/orders/${orderId}/decrease`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ nextQuantity }),
    });
    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      throw new Error(
        (errData as Record<string, string>).error ?? `Decrease failed (${res.status})`,
      );
    }
    get().refreshOrders().catch(() => {});
  },

  replaceOrder: async (
    orderId: string,
    newPriceCents: number,
    newQuantity?: number,
    forceCancelReplace?: boolean,
  ) => {
    // (0, 100) exclusive — sub-cent prices (58.3¢) are valid on deci_cent
    // markets. Tick alignment is NOT left to the venue on this path: the
    // validateReplacePriceCents gate below rejects a misaligned CHANGED price
    // client-side, before the DELETE, because a venue rejection would land
    // after the resting order was already cancelled.
    if (!Number.isFinite(newPriceCents) || newPriceCents <= 0 || newPriceCents >= 100) {
      throw new Error('Replacement price must be above 0 and below 100¢.');
    }
    const existing = get().openOrders.find((o) => o.orderId === orderId);
    if (!existing) {
      throw new Error('Order to replace was not found in the open-orders list.');
    }
    if (!canEditKalshiOrder(existing)) {
      throw new Error('This recovered order can be cancelled, but it cannot be edited safely.');
    }
    if (existing.type !== 'limit') {
      throw new Error('Only resting limit orders can be replaced.');
    }
    if (!Number.isFinite(existing.remainingCount) || existing.remainingCount <= 0) {
      throw new Error('Order remaining size is missing or invalid.');
    }
    // Resolve target quantity. Omitted → keep existing remaining. Validate
    // the same shape decreaseOrder enforces, here too, so an invalid value
    // can't slip into the cancel+place branch either.
    const targetQty = newQuantity ?? existing.remainingCount;
    if (!Number.isFinite(targetQty) || !Number.isInteger(targetQty) || targetQty < 1) {
      throw new Error('Replacement quantity must be a positive integer ≥ 1.');
    }
    // Side-native current price for the smart-route check. Kalshi often
    // returns only yes_price even for NO orders, so mirror the table/card
    // fallback and invert (exact mirror) when the matching side price is absent.
    const currentPriceCents =
      existing.side === 'yes'
        ? (existing.yesPrice ?? (existing.noPrice != null ? mirrorCents(existing.noPrice) : null))
        : (existing.noPrice ?? (existing.yesPrice != null ? mirrorCents(existing.yesPrice) : null));
    if (currentPriceCents != null && newPriceCents === currentPriceCents && targetQty === existing.remainingCount) {
      return { orderId: existing.orderId };
    }
    // Smart route: a pure quantity-shrink at the same price stays queue-
    // preserving via the /decrease endpoint. Anything else (price moved,
    // qty increased, qty kept but price moved) routes through cancel+place
    // and loses queue position. Polymarket has no /decrease so its replace
    // is always cancel+place (handled in usePolymarketRelayOrders).
    //
    // The caller can opt OUT of the smart route via forceCancelReplace,
    // when the trader explicitly wants to lose queue position (e.g. to
    // refresh / cycle to the back). Modal exposes that as a checkbox.
    if (
      !forceCancelReplace &&
      currentPriceCents != null &&
      newPriceCents === currentPriceCents &&
      targetQty < existing.remainingCount
    ) {
      await get().decreaseOrder(orderId, targetQty);
      return { orderId: existing.orderId };
    }
    // Last-line tick revalidation BEFORE the cancel (DNL round-3 finding 1):
    // a venue tick rejection on the replacement lands after the original is
    // gone. Hydra's Kalshi priceRanges refresh through discovery and can
    // briefly trail a live tick-regime change, so a CHANGED cancel-first price
    // remains whole-cent-only here (valid across published structures). The
    // unchanged current price always passes by existence. New-order surfaces
    // use the range contract because a venue rejection there loses no order.
    const tickCheck = validateReplacePriceCents({
      newPriceCents,
      currentPriceCents,
      tickCents: null,
      unknownTickPolicy: 'whole-cent',
    });
    if (!tickCheck.ok) {
      throw new Error(tickCheck.error);
    }
    // 1) Cancel the original. If this rejects, the original is still
    // resting — surface the error and bail before touching place.
    try {
      // The UNFENCED leg (#241): an edit must not arm a cancel fence on the
      // order it is replacing — that would disable the row's own Edit control
      // and could raise the cancel-timeout notice on a successful edit.
      await requestKalshiCancel(orderId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Cancel failed';
      throw new Error(`Could not cancel original order: ${message}`);
    }
    // 2) Place new at the new price + qty, preserving every other param.
    // post_only=true is the safe default for an edit so the replacement
    // can't accidentally become a market sweep. Trade-guard / limit
    // enforcement runs at the relay's checkOrderLimit chokepoint; the
    // browser-side useTradeGuard wraps the dashboard trade-ticket path
    // but is not on this edit path.
    const expirationTime =
      existing.expirationTs != null
        ? new Date(existing.expirationTs * 1000).toISOString()
        : undefined;
    let placed: KalshiOrder;
    try {
      placed = await get().placeOrder({
        ticker: existing.ticker,
        side: existing.side,
        action: existing.action,
        type: 'limit',
        count: targetQty,
        price: newPriceCents,
        postOnly: true,
        timeInForce: 'good_till_canceled',
        ...(expirationTime !== undefined ? { expirationTime } : {}),
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Place failed';
      throw new Error(
        `Original order was cancelled, but the replacement failed to place: ${message}`,
      );
    }
    // placeOrder falls back to '' when the venue response carries no id —
    // the replacement may be resting, but nothing downstream can key off it.
    if (!placed.orderId) {
      throw new Error(
        'Replacement was submitted but the venue returned no order id — refresh open orders to reconcile.',
      );
    }
    return { orderId: placed.orderId };
  },
}));

// ── relay push integration ──────────────────────────────────────────────────
// Frames carry the SAME payloads as the REST endpoints the poll tick hits, so
// the existing parsers consume them unchanged. Every handler gates on
// status==='connected' — a frame racing a disconnect can't resurrect state
// (same job the poll path's generation guard does). Fills route through
// processFill for dedup/sounds/ticker exactly like polled fills.

subscribePush('positions', (payload) => {
  if (useAccountStore.getState().status !== 'connected') return;
  useAccountStore.setState({ positions: parsePositions(payload as Record<string, unknown>) });
});

subscribePush('orders', (payload) => {
  const st = useAccountStore.getState();
  if (st.status !== 'connected') return;
  const orders = parseOrdersSnapshot(payload);
  if (orders === null) {
    const failures = st.ordersSync.consecutiveFailures + 1;
    console.warn(
      `[accountStore] ${new Date().toISOString()} source=push open-orders refresh failed (${failures}x): malformed orders payload`,
    );
    useAccountStore.setState({
      ordersSync: {
        ...st.ordersSync,
        lastErrorMs: Date.now(),
        lastError: 'malformed orders payload',
        consecutiveFailures: failures,
      },
    });
    return;
  }
  const reconciledOrders = reconcilePlacementConfirmations(orders);
  if (reconciledOrders.length === 0) logOpenOrdersEmptyTransition('push', st.openOrders.length);
  useAccountStore.setState({
    openOrders: reconciledOrders,
    kalshiCancelStates: reconcileCancelFence(st.kalshiCancelStates, orders),
    // Stream frames are the orders-sync heartbeat now — the staleness badge
    // (Jun 11 incident guard) reflects push freshness in push mode.
    ordersSync: {
      lastSuccessMs: Date.now(),
      lastErrorMs: st.ordersSync.lastErrorMs,
      lastError: st.ordersSync.lastError,
      consecutiveFailures: 0,
    },
  });
});

subscribePush('pnl', (payload) => {
  const st = useAccountStore.getState();
  if (st.status !== 'connected') return;
  const p = payload as {
    realizedPnlCents?: number;
    realizedFeesCents?: number | null;
    realizedPnlNetCents?: number | null;
    openNotionalCents?: number;
  };
  useAccountStore.setState({
    realizedPnlCents: p.realizedPnlCents ?? st.realizedPnlCents,
    realizedFeesCents:
      p.realizedFeesCents === undefined ? st.realizedFeesCents : p.realizedFeesCents,
    realizedPnlNetCents:
      p.realizedPnlNetCents === undefined ? st.realizedPnlNetCents : p.realizedPnlNetCents,
    openNotionalCents: p.openNotionalCents ?? st.openNotionalCents,
  });
});

subscribePush('fills', (payload) => {
  const st = useAccountStore.getState();
  if (st.status !== 'connected') return;
  // Fail-closed (#499): a malformed push frame is dropped whole rather than
  // defaulted into a synthetic row. Push fills are incremental, not a history
  // read, so they never latch fillsSynced.
  const fills = parseFillsSnapshot(payload);
  if (fills === null) return;
  for (const fill of fills) st.processFill(fill);
});
